import React, {useEffect, useState} from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const markerIcon = new L.Icon({
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconSize: [12.5, 21],
    iconAnchor: [6.25, 21],
    popupAnchor: [1, -34],
});

const MapUpdater = ({ position }) => {
    const map = useMap();
    if (position) {
        map.flyTo(position, 13);
    }
    return null;
};
const fetchWithFallback = async (lat, lng, query = null) => {
    const isReverse = !query; // If no query, we are doing reverse (click on map)

    // URLs
    const photonUrl = isReverse
        ? `https://photon.komoot.io/reverse?lon=${lng}&lat=${lat}`
        : `https://photon.komoot.io/api/?q=${encodeURIComponent(query)}&limit=5`;

    const nominatimUrl = isReverse
        ? `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`
        : `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}`;

    try {
        // --- 1. TRY PHOTON ---
        const response = await fetch(photonUrl);
        if (!response.ok) throw new Error("Photon failed");
        const data = await response.json();

        if (data.features && data.features.length > 0) {
            return data.features.map(f => ({
                lat: f.geometry.coordinates[1],
                lng: f.geometry.coordinates[0], // Use lng
                lon: f.geometry.coordinates[0], // Add lon as a backup
                display_name: [f.properties.name, f.properties.city, f.properties.country].filter(Boolean).join(", ")
            }));
        }
        throw new Error("No results from Photon");

    } catch (error) {
        // --- 2. FALLBACK TO NOMINATIM ---
        console.warn("Photon failed or no results, falling back to Nominatim...");
        try {
            const response = await fetch(nominatimUrl, {
                headers: { 'User-Agent': 'GalacticPlanner/1.0' } // Identify your app
            });
            const data = await response.json();

            // Nominatim returns an object for reverse, but an array for search
            const results = Array.isArray(data) ? data : [data];
            return results.map(item => ({
                lat: parseFloat(item.lat),
                lng: parseFloat(item.lon),
                display_name: item.display_name
            }));
        } catch (err) {
            return []; // Both failed
        }
    }
};

const BirthplaceMap = ({ onLocationSelect, initialLocation, initialLocationName }) => {
    const [position, setPosition] = useState(initialLocation);
    const [searchQuery, setSearchQuery] = useState(initialLocationName);
    const [suggestions, setSuggestions] = useState([]);
    const [typingTimer, setTypingTimer] = useState(null);

    useEffect(() => {
        if (initialLocation) {
            setPosition(initialLocation);
        }
        if (initialLocationName) {
            setSearchQuery(initialLocationName);
        }
    }, [initialLocation, initialLocationName]);
    const MapEvents = () => {
        useMapEvents({
            click(e) {
                handleMapClick(e.latlng);
            },
        });
        return null;
    };

    const handleMapClick = async (latlng) => {
        const results = await fetchWithFallback(latlng.lat, latlng.lng);
        if (results.length > 0) {
            const bestMatch = results[0];
            setPosition({ lat: bestMatch.lat, lng: bestMatch.lng });
            setSearchQuery(bestMatch.display_name);
            onLocationSelect({ lat: bestMatch.lat, lng: bestMatch.lng }, bestMatch.display_name);
        }
    };

    const searchLocation = async (query) => {
        const results = await fetchWithFallback(null, null, query);
        setSuggestions(results);
    };

    const handleSearchChange = (e) => {
        const query = e.target.value;
        setSearchQuery(query);

        // Clear the previous timer
        if (typingTimer) clearTimeout(typingTimer);

        if (query.length > 2) {
            // Start a new timer: Only search after 500ms of no typing
            const newTimer = setTimeout(() => {
                searchLocation(query);
            }, 500);
            setTypingTimer(newTimer);
        } else {
            setSuggestions([]);
        }
    };

    const handleSuggestionSelect = (suggestion) => {
        // Look for lat, then lon OR lng (handling both naming conventions)
        const { lat, lon, lng, display_name } = suggestion;

        // Determine the valid longitude
        const validLng = lon || lng;

        if (lat !== undefined && validLng !== undefined) {
            const newCenter = { lat: parseFloat(lat), lng: parseFloat(validLng) };
            setPosition(newCenter);
            setSearchQuery(display_name);
            setSuggestions([]);
            onLocationSelect(newCenter, display_name);
        } else {
            console.error("Invalid coordinates in suggestion:", suggestion);
        }
    };

    return (
        <>
            {/* Search input */}
            {/* Search input with unclipped glow */}
            <div className="relative">
                {/* the glow lives outside the input so it can't be clipped */}
                <span
                    className="pointer-events-none absolute -inset-1 rounded-xl opacity-0
               transition-opacity duration-150
               group-focus-within:opacity-100"
                    style={{ boxShadow: '0 0 0 3px rgba(250,204,21,0.55)' }}
                />
                <div className="group">
                    <input
                        type="text"
                        value={searchQuery}
                        placeholder="Search for a location"
                        onKeyDown={(e) => {
                            // Check if Enter was pressed AND if there are suggestions available
                            if (e.key === 'Enter' && suggestions.length > 0) {
                                e.preventDefault(); // Prevent form submission if inside a form
                                handleSuggestionSelect(suggestions[0]);
                            }
                        }}
                        onChange={handleSearchChange}
                        className="relative z-10 mt-1 block w-full rounded-lg bg-black/30 text-white
                 placeholder-indigo-200/70 border border-white/10 px-3 py-3 shadow-sm
                 focus:outline-none"  // no ring here
                    />
                </div>
            </div>



            {/* Suggestions dropdown */}
            {suggestions.length > 0 && (
                <ul
                    className="mt-2 max-h-60 overflow-auto rounded-lg border border-white/10 bg-black/50
                   backdrop-blur-md shadow-xl"
                >
                    {suggestions.map((suggestion) => (
                        <li
                            key={suggestion.place_id}
                            className="p-2 text-indigo-100 cursor-pointer transition-colors
                       hover:bg-white/10 hover:text-white"
                            onClick={() => handleSuggestionSelect(suggestion)}
                        >
                            {suggestion.display_name}
                        </li>
                    ))}
                </ul>
            )}

            {/* Map container (glass card) */}
            <div
                className="mt-3 block w-full rounded-2xl border border-white/10 bg-white/10
                 backdrop-blur-md shadow-[0_20px_50px_-12px_rgba(250,204,21,0.25)]"
            >
                <MapContainer
                    center={[0, 0]}
                    zoom={1}
                    style={{ height: '400px', width: '100%', borderRadius: '1rem', overflow: 'hidden' }}
                >
                    <TileLayer
                        url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
                        noWrap={true}
                    />
                    {position && <Marker position={position} icon={markerIcon} />}
                    <MapEvents />
                    <MapUpdater position={position} />
                </MapContainer>
            </div>
        </>
    );

};

export default BirthplaceMap;
